import cx from 'classnames'

import { SHARING_BUTTONS_MAP } from '@/legacy/blocks/SharingButtons/constants'
import { Typography } from '@/legacy/components/Typography'
import { withContainer } from '@/legacy/hocs/withContainer'
import { useDesktopDetector } from '@/legacy/hooks/useDeviceDetector'

import { SimpleAnnouncementBar as VisualNotifier } from '../AnnouncementBar'
import { ScreenReaderNotifier as AccessibleNotifier } from '../ScreenReaderNotifier'

import { ButtonWithIcon } from './ButtonWithIcon'
import {
  ListItem,
  SharingButtonsContainer,
  SharingButtonsList,
  SharingButtonsWrapper,
} from './styles'
import type { ButtonProps, SharingButtonsComponentProps } from './types'
import { LIST_LABEL, NOTIFICATION_DEFAULT_PROPS, SHARING_LABEL, useSharingButtons } from './utils'

const SharingButtonsComponent = ({
  className,
  buttonsContent = SHARING_BUTTONS_MAP,
  position = 'left',
  variations = 'ghost-icons',
  sharingLabel = SHARING_LABEL,
  notification = NOTIFICATION_DEFAULT_PROPS,
  useDeviceHook = useDesktopDetector,
}: SharingButtonsComponentProps) => {
  const isDesktop = useDeviceHook()
  const { showMessageNotifier, handleOnClick } = useSharingButtons()
  const shouldDisplayLabel = sharingLabel?.length > 0 && variations === 'gray-icons'
  const buttonsProps = { isDesktop, buttonsContent, handleOnClick }

  return (
    <SharingButtonsContainer className={cx({ [`--${variations}`]: variations }, className)}>
      {shouldDisplayLabel && <Typography tag="h3" html={sharingLabel} emphasys />}
      <SharingButtonsWrapper position={position}>
        <Buttons {...buttonsProps} />
      </SharingButtonsWrapper>
      {!isDesktop && (
        <AccessibleNotifier
          label={notification.message}
          enabled={showMessageNotifier}
          allowSuppressOtherElements={false}
        >
          <VisualNotifier background="gray" colorModifier="dark" {...notification} />
        </AccessibleNotifier>
      )}
    </SharingButtonsContainer>
  )
}

const Buttons = ({ isDesktop, buttonsContent, handleOnClick }: ButtonProps) => (
  <SharingButtonsList aria-label={LIST_LABEL}>
    {Object.entries(buttonsContent).map(([key, props]) => {
      const shouldHideButton = isDesktop && !props.sharerUrl
      if (shouldHideButton) return null

      return (
        <ListItem key={key}>
          <ButtonWithIcon
            handleOnClick={handleOnClick}
            sharerUrl={props.sharerUrl}
            buttonIcon={props.buttonIcon}
            accessibleLabel={props.accessibleLabel}
          />
        </ListItem>
      )
    })}
  </SharingButtonsList>
)

export default withContainer(SharingButtonsComponent)
